/**
 * CR054: Snapshot Comparison Panel
 * Compares frozen prompt snapshot from run start to current live pack
 */

import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, GitCompare, Check, AlertTriangle, Minus, Plus, Eye } from "lucide-react";
import { toast } from "sonner";

interface PromptSnapshot {
  prompt_id: string;
  key: string;
  phase: string;
  prompt_text: string;
  enabled: boolean;
  order_index: number;
  model_override: string | null;
  temperature_override: number | null;
  max_tokens_override: number | null;
  version?: number;
  prompt_hash?: string;
}

interface LivePrompt {
  id: string;
  key: string;
  phase: string;
  prompt_text: string;
  version: number;
  prompt_hash: string | null;
}

interface PackItemRow {
  id: string;
  prompt_id: string;
  order_index: number;
  enabled: boolean;
  model_override: string | null;
  temperature_override: number | null;
}

interface PackItem extends PackItemRow {
  prompt?: LivePrompt;
}

type ComparisonStatus = "SAME" | "CHANGED" | "REMOVED" | "ADDED" | "MISSING";

interface ComparisonRow {
  key: string;
  phase: string;
  snapshotVersion?: number;
  snapshotHash?: string;
  currentVersion?: number;
  currentHash?: string;
  status: ComparisonStatus;
  snapshotEnabled?: boolean;
  currentEnabled?: boolean;
  snapshotText?: string;
  currentText?: string;
  snapshotOrderIndex?: number;
  currentOrderIndex?: number;
}

const STATUS_STYLES: Record<ComparisonStatus, { color: string; icon: typeof Check; label: string }> = {
  SAME: { color: "bg-green-500/20 text-green-700", icon: Check, label: "Same" },
  CHANGED: { color: "bg-yellow-500/20 text-yellow-700", icon: AlertTriangle, label: "Changed" },
  REMOVED: { color: "bg-red-500/20 text-red-700", icon: Minus, label: "Removed" },
  ADDED: { color: "bg-blue-500/20 text-blue-700", icon: Plus, label: "Added" },
  MISSING: { color: "bg-gray-500/20 text-gray-700", icon: AlertTriangle, label: "Missing (Archived)" },
};

const PHASE_COLORS: Record<string, string> = {
  SYSTEM: "bg-gray-500",
  SCHEMA: "bg-blue-500",
  READ: "bg-cyan-500",
  SCAN: "bg-green-500",
  EXTRACT: "bg-yellow-500",
  NORMALIZE: "bg-purple-500",
  DEDUPE: "bg-pink-500",
  VALIDATE: "bg-orange-500",
};

interface SnapshotComparisonPanelProps {
  promptPackId: string | null;
  promptSnapshot: PromptSnapshot[];
  snapshotHash?: string | null;
  snapshotCreatedAt?: string | null;
}

export function SnapshotComparisonPanel({
  promptPackId,
  promptSnapshot,
  snapshotHash,
  snapshotCreatedAt,
}: SnapshotComparisonPanelProps) {
  const [loading, setLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [currentPackItems, setCurrentPackItems] = useState<PackItem[]>([]);
  const [comparisonRows, setComparisonRows] = useState<ComparisonRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<ComparisonRow | null>(null);
  const [showDiffDialog, setShowDiffDialog] = useState(false);

  const fetchCurrentPack = async () => {
    if (!promptPackId) {
      toast.error("No prompt pack linked to this run");
      return;
    }

    setLoading(true);
    try {
      // Fetch current pack items - cast to any to avoid TS2589 type inference issues
      const { data: itemsRaw, error: itemsError } = await (supabase as any)
        .from("prompt_pack_items")
        .select("*")
        .eq("pack_id", promptPackId)
        .order("order_index");

      if (itemsError) throw itemsError;

      const items: PackItemRow[] = (itemsRaw || []).map((item: any) => ({
        id: item.id,
        prompt_id: item.prompt_id,
        order_index: item.order_index,
        enabled: item.enabled,
        model_override: item.model_override,
        temperature_override: item.temperature_override,
      }));

      if (items.length === 0) {
        setCurrentPackItems([]);
        setShowComparison(true);
        return;
      }

      // Fetch prompts separately
      const promptIds = items.map((item) => item.prompt_id);
      const { data: promptsRaw, error: promptsError } = await supabase
        .from("prompts")
        .select("*")
        .in("id", promptIds);

      if (promptsError) throw promptsError;

      const prompts: LivePrompt[] = (promptsRaw || []).map((p: any) => ({
        id: p.id,
        key: p.key,
        phase: p.phase,
        prompt_text: p.prompt_text,
        version: p.version || 1,
        prompt_hash: p.prompt_hash,
      }));

      // Merge items with prompts
      const promptMap = new Map((prompts || []).map((p) => [p.id, p]));
      const mergedItems: PackItem[] = items.map((item) => ({
        ...item,
        prompt: promptMap.get(item.prompt_id),
      }));

      setCurrentPackItems(mergedItems);
      setShowComparison(true);
    } catch (err) {
      console.error("Error fetching current pack:", err);
      toast.error("Failed to load current pack");
    } finally {
      setLoading(false);
    }
  };

  // Compute comparison when we have both snapshot and current data
  useEffect(() => {
    if (!showComparison) return;

    const rows: ComparisonRow[] = [];
    const snapshotMap = new Map(promptSnapshot.map((p) => [p.key, p]));
    const currentMap = new Map(
      currentPackItems
        .filter((item) => item.prompt)
        .map((item) => [item.prompt!.key, { item, prompt: item.prompt! }])
    );

    // Check all snapshot prompts
    for (const snap of promptSnapshot) {
      const current = currentMap.get(snap.key);

      if (!current) {
        rows.push({
          key: snap.key,
          phase: snap.phase,
          snapshotVersion: snap.version,
          snapshotHash: snap.prompt_hash,
          snapshotEnabled: snap.enabled,
          snapshotText: snap.prompt_text,
          snapshotOrderIndex: snap.order_index,
          status: "REMOVED",
        });
      } else {
        const hashMatch =
          snap.prompt_hash === current.prompt.prompt_hash ||
          (!snap.prompt_hash && !current.prompt.prompt_hash);
        const versionMatch = snap.version === current.prompt.version;
        const textMatch = snap.prompt_text === current.prompt.prompt_text;

        const isSame = hashMatch || (versionMatch && textMatch);

        rows.push({
          key: snap.key,
          phase: snap.phase,
          snapshotVersion: snap.version,
          snapshotHash: snap.prompt_hash,
          currentVersion: current.prompt.version,
          currentHash: current.prompt.prompt_hash || undefined,
          snapshotEnabled: snap.enabled,
          currentEnabled: current.item.enabled,
          snapshotText: snap.prompt_text,
          currentText: current.prompt.prompt_text,
          snapshotOrderIndex: snap.order_index,
          currentOrderIndex: current.item.order_index,
          status: isSame ? "SAME" : "CHANGED",
        });
      }
    }

    // Check for newly added prompts
    for (const [key, { item, prompt }] of currentMap) {
      if (!snapshotMap.has(key)) {
        rows.push({
          key: prompt.key,
          phase: prompt.phase,
          currentVersion: prompt.version,
          currentHash: prompt.prompt_hash || undefined,
          currentEnabled: item.enabled,
          currentText: prompt.prompt_text,
          currentOrderIndex: item.order_index,
          status: "ADDED",
        });
      }
    }

    // Sort by phase order, then by key
    const phaseOrder = ["READ", "SYSTEM", "SCHEMA", "SCAN", "EXTRACT", "NORMALIZE", "DEDUPE", "VALIDATE"];
    rows.sort((a, b) => {
      const phaseA = phaseOrder.indexOf(a.phase);
      const phaseB = phaseOrder.indexOf(b.phase);
      if (phaseA !== phaseB) return phaseA - phaseB;
      return a.key.localeCompare(b.key);
    });

    setComparisonRows(rows);
  }, [showComparison, currentPackItems, promptSnapshot]);

  const summary = useMemo(() => {
    const counts = { SAME: 0, CHANGED: 0, REMOVED: 0, ADDED: 0, MISSING: 0 };
    for (const row of comparisonRows) {
      counts[row.status]++;
    }
    return counts;
  }, [comparisonRows]);

  const handleViewDiff = (row: ComparisonRow) => {
    setSelectedRow(row);
    setShowDiffDialog(true);
  };

  if (promptSnapshot.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitCompare className="h-5 w-5" />
                Prompt Snapshot Comparison
              </CardTitle>
              <CardDescription className="mt-1">
                Compare frozen snapshot to current pack state
              </CardDescription>
            </div>
            {!showComparison && (
              <Button onClick={fetchCurrentPack} disabled={loading || !promptPackId}>
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GitCompare className="mr-2 h-4 w-4" />
                )}
                Compare to Current Pack
              </Button>
            )}
          </div>
        </CardHeader>

        {showComparison && (
          <CardContent>
            {/* Summary badges */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {summary.SAME > 0 && (
                <Badge className={STATUS_STYLES.SAME.color}>
                  <Check className="h-3 w-3 mr-1" />
                  {summary.SAME} Same
                </Badge>
              )}
              {summary.CHANGED > 0 && (
                <Badge className={STATUS_STYLES.CHANGED.color}>
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {summary.CHANGED} Changed
                </Badge>
              )}
              {summary.REMOVED > 0 && (
                <Badge className={STATUS_STYLES.REMOVED.color}>
                  <Minus className="h-3 w-3 mr-1" />
                  {summary.REMOVED} Removed
                </Badge>
              )}
              {summary.ADDED > 0 && (
                <Badge className={STATUS_STYLES.ADDED.color}>
                  <Plus className="h-3 w-3 mr-1" />
                  {summary.ADDED} Added
                </Badge>
              )}
            </div>

            {/* Metadata */}
            {(snapshotHash || snapshotCreatedAt) && (
              <div className="text-xs text-muted-foreground mb-4 space-y-1">
                {snapshotHash && (
                  <p>
                    <span className="font-medium">Snapshot Hash:</span>{" "}
                    <code className="bg-muted px-1 rounded">{snapshotHash.substring(0, 12)}</code>
                  </p>
                )}
                {snapshotCreatedAt && (
                  <p>
                    <span className="font-medium">Created:</span>{" "}
                    {new Date(snapshotCreatedAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Comparison table */}
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Phase</TableHead>
                    <TableHead>Prompt Key</TableHead>
                    <TableHead className="w-[100px]">Snapshot Ver</TableHead>
                    <TableHead className="w-[100px]">Current Ver</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonRows.map((row) => {
                    const StatusIcon = STATUS_STYLES[row.status].icon;
                    return (
                      <TableRow key={row.key}>
                        <TableCell>
                          <Badge className={PHASE_COLORS[row.phase] || "bg-gray-500"}>
                            {row.phase}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.key}</TableCell>
                        <TableCell>
                          {row.snapshotVersion !== undefined ? (
                            <span className="text-sm">
                              v{row.snapshotVersion}
                              {row.snapshotHash && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({row.snapshotHash.substring(0, 6)})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.currentVersion !== undefined ? (
                            <span className="text-sm">
                              v{row.currentVersion}
                              {row.currentHash && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({row.currentHash.substring(0, 6)})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[row.status].color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_STYLES[row.status].label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {(row.status === "CHANGED" || row.snapshotText || row.currentText) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDiff(row)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setShowComparison(false)}>
                Close Comparison
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Diff Dialog */}
      <Dialog open={showDiffDialog} onOpenChange={setShowDiffDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge className={PHASE_COLORS[selectedRow?.phase || ""] || "bg-gray-500"}>
                {selectedRow?.phase}
              </Badge>
              {selectedRow?.key}
              {selectedRow && (
                <Badge className={STATUS_STYLES[selectedRow.status].color}>
                  {STATUS_STYLES[selectedRow.status].label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Comparing snapshot version to current version
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2 text-sm flex items-center gap-2">
                Snapshot
                {selectedRow?.snapshotVersion !== undefined && (
                  <Badge variant="outline">v{selectedRow.snapshotVersion}</Badge>
                )}
              </h4>
              <ScrollArea className="h-[400px] border rounded-lg">
                <pre className="text-xs p-4 whitespace-pre-wrap bg-muted/50">
                  {selectedRow?.snapshotText || "(Not present in snapshot)"}
                </pre>
              </ScrollArea>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-sm flex items-center gap-2">
                Current
                {selectedRow?.currentVersion !== undefined && (
                  <Badge variant="outline">v{selectedRow.currentVersion}</Badge>
                )}
              </h4>
              <ScrollArea className="h-[400px] border rounded-lg">
                <pre className="text-xs p-4 whitespace-pre-wrap bg-muted/50">
                  {selectedRow?.currentText || "(Not present in current pack)"}
                </pre>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
